import { Bell, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGameNotifications } from "@/hooks/useGameNotifications";
import { useFavoriteNationAlerts } from "@/hooks/useFavoriteNationAlerts";
import { format } from "date-fns";

const NotificationBell = () => {
  const { reminders, removeReminder, permission, requestPermission, isSupported } = useGameNotifications();
  const { 
    alertsEnabled, 
    toggleAlerts, 
    favoriteNation, 
    upcomingGames,
    hasNation 
  } = useFavoriteNationAlerts();

  if (!isSupported) return null;

  const handleEnableNotifications = async () => {
    await requestPermission();
  };

  const totalAlerts = reminders.length + (alertsEnabled && hasNation ? upcomingGames.length : 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Game reminders">
          <Bell className="h-5 w-5" />
          {totalAlerts > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
              {totalAlerts}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-popover border-border z-50">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-foreground">Game Alerts</h4>
          {permission !== "granted" && (
            <Button variant="outline" size="sm" onClick={handleEnableNotifications}>
              Enable
            </Button>
          )}
        </div>

        {permission === "denied" && (
          <p className="text-sm text-muted-foreground mb-3">
            Notifications are blocked. Please enable them in your browser settings.
          </p>
        )}

        {/* Favorite Nation Alerts Toggle */}
        {hasNation && favoriteNation && (
          <>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">{favoriteNation.flag}</span>
                <div>
                  <Label htmlFor="nation-alerts" className="text-sm font-medium cursor-pointer">
                    {favoriteNation.name} Alerts
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Auto-notify for Olympics games
                  </p>
                </div>
              </div>
              <Switch
                id="nation-alerts"
                checked={alertsEnabled}
                onCheckedChange={toggleAlerts}
              />
            </div>

            {/* Upcoming nation games */}
            {alertsEnabled && upcomingGames.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <Flag className="h-3 w-3" />
                  Upcoming {favoriteNation.name} games
                </p>
                <div className="space-y-1.5">
                  {upcomingGames.slice(0, 3).map((game) => (
                    <div
                      key={game.id}
                      className="flex items-center justify-between p-2 rounded bg-primary/5 border border-primary/20 text-xs"
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{game.homeFlag}</span>
                        <span className="font-medium">{game.homeTeam}</span>
                        <span className="text-muted-foreground">vs</span>
                        <span className="font-medium">{game.awayTeam}</span>
                        <span>{game.awayFlag}</span>
                      </div>
                      <div className="text-muted-foreground">
                        {format(new Date(game.date), "MMM d")} {game.time}
                      </div>
                    </div>
                  ))}
                  {upcomingGames.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">
                      +{upcomingGames.length - 3} more games
                    </p>
                  )}
                </div>
              </div>
            )}

            <Separator className="my-3" />
          </>
        )}

        {/* Manual reminders section */}
        <p className="text-xs text-muted-foreground mb-2">Manual Reminders</p>
        
        {reminders.length === 0 ? (
          <div className="text-center py-4">
            <Bell className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No manual reminders set</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click on a game to set a reminder
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[200px]">
            <div className="space-y-2">
              {reminders.map((reminder) => {
                const gameDate = new Date(reminder.gameDate + "T00:00:00");
                return (
                  <div
                    key={reminder.gameId}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">
                        {reminder.awayTeam} @ {reminder.homeTeam}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          {reminder.league}
                        </Badge>
                        <span>{format(gameDate, "MMM d")}</span>
                        <span>•</span>
                        <span>{reminder.gameTime}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeReminder(reminder.gameId)}
                    >
                      ×
                    </Button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        <p className="text-xs text-muted-foreground mt-3 text-center">
          {alertsEnabled && hasNation
            ? "Nation alerts: 30 min before • Manual: 15 min before"
            : "You'll be notified 15 minutes before each game"}
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
